import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import { logConsole, logTransaction } from '../common/log';
import { handleThunkError } from '../common/util';
import { LogType } from '../models/log.model';
import { IOdataResult } from '../models/odata.model';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { setIsLoadingCountState } from './appSlice';
import { IFranchiseSyncStatus } from '../models/dms/franchise.model';
import { getTypecodesByType } from '../api/dms/typecode.api';
import { ITypeCode } from '../models/dms/typecode.model';

interface IFranchiseSyncState {
  franchisesSync?: IFranchiseSyncStatus;
  franchisesSyncLoading: boolean;
}

const initialState: IFranchiseSyncState = {
  franchisesSync: {
    hasSync: false,
    timestamp: 0,
    isProcessing: false,
    data: {} as IOdataResult<ITypeCode>,
  },
  franchisesSyncLoading: false,
};

/// THUNKS

export const setFranchisesSyncStoreAsync = createAsyncThunk<{}, IFranchiseSyncStatus | undefined>('franchiseSync/setFranchiseesSyncStoreAsync', async (val, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting franchises to storage`, '[franchiseSync] setFranchiseesSyncStoreAsync');
  try {
    dispatch(setFranchisesSyncState(val));
    await updateConfigObject<IFranchiseSyncStatus>(ConfigName.FranchisesSync, val || ({} as IFranchiseSyncStatus), false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setFranchisesProcessingStoreAsync = createAsyncThunk<{}, boolean>('franchiseSync/setFranchiseesProcessingStoreAsync', async (val, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, '', '[franchiseSync] setFranchiseesProcessingStoreAsync');
  try {
    dispatch(setFranchisesProcessingState(val));
    await updateConfigObject<IFranchiseSyncStatus>(
      ConfigName.FranchisesSync,
      {
        ...(getState() as RootState).franchiseSync.franchisesSync,
        isProcessing: val,
      } as IFranchiseSyncStatus,
      false,
    );
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setFranchisesHasSyncStoreAsync = createAsyncThunk<{}, boolean>('franchiseSync/setFranchiseesHasSyncStoreAsync', async (val, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, '', '[franchiseSync] setFranchiseesHasSyncStoreAsync');
  try {
    dispatch(setFranchisesHasSyncState(val));
    await updateConfigObject<IFranchiseSyncStatus>(
      ConfigName.FranchisesSync,
      {
        ...(getState() as RootState).franchiseSync.franchisesSync,
        hasSync: val,
        isProcessing: false,
      } as IFranchiseSyncStatus,
      false,
    );
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const fetchFranchisesStoreAsync = createAsyncThunk<{}, void>('franchiseSync/fetchFranchiseesStoreAsync', async (nil, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Fetching franchises from database`, '[franchiseSync] fetchFranchiseesStoreAsync');
  try {
    dispatch(setIsLoadingCountState(1));
    dispatch(setFranchisesSyncLoadingState(true));

    const state = getState() as RootState;
    const { franchisesSync } = state.franchiseSync;

    let franchisesSyncState = { ...franchisesSync } as IFranchiseSyncStatus;

    try {
      const responseData = await getTypecodesByType('FC');

      franchisesSyncState = {
        ...franchisesSyncState,
        timestamp: new Date().getTime(),
        hasSync: true,
        isProcessing: false,
        data: { ...responseData },
      };

      dispatch(setFranchisesSyncState(franchisesSyncState));
      await updateConfigObject<IFranchiseSyncStatus>(ConfigName.FranchisesSync, franchisesSyncState, false);
      return responseData;
    } catch (ex: any) {
      logTransaction(LogType.Error, `Failed to execute fetchFranchiseesStoreAsync thunk. ${ex?.message}`);
    } finally {
      dispatch(setIsLoadingCountState(-1));
      dispatch(setFranchisesSyncLoadingState(false));
    }
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setFranchisesSyncLoadingState(false));
  }
});

/// SLICE

export const franchiseSyncSlice = createSlice({
  name: 'franchiseSync',
  initialState,
  reducers: {
    setFranchisesSyncState: (state, action: PayloadAction<IFranchiseSyncStatus | undefined>) => {
      logConsole(LogType.Info, '[franchiseSync] setFranchiseesSyncState');
      state.franchisesSync = action.payload;
    },
    setFranchisesProcessingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[franchiseSync] setFranchiseesProcessingState: ${action.payload}`);
      if (state.franchisesSync) {
        state.franchisesSync.isProcessing = action.payload;
      }
    },
    setFranchisesHasSyncState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[franchiseSync] setFranchiseesHasSyncState: ${action.payload}`);
      if (state.franchisesSync) {
        state.franchisesSync.hasSync = action.payload;
        state.franchisesSync.isProcessing = false;
      }
    },
    setFranchisesSyncLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[franchiseSync] setFranchiseesSyncLoadingState: ${action.payload}`);
      state.franchisesSyncLoading = action.payload;
    },
  },
});

/// REDUCERS

export const { setFranchisesSyncState, setFranchisesProcessingState, setFranchisesHasSyncState, setFranchisesSyncLoadingState } = franchiseSyncSlice.actions;

export default franchiseSyncSlice.reducer;
